// The shared cast timeline. Spec: docs/superpowers/specs/2026-10-12-cast-sync-protocol-design.md §1.
import type { PlayStatus, StreamSlot } from "../../types/stream";

export interface CastTimeline {
  status: PlayStatus;
  seekMs: number;
  /** Server epoch ms when play (re)starts; null when the server sent none. */
  startAt: number | null;
  /** null: an old server without `timelineRev` — every change is applied. */
  rev: number | null;
}

const DIGITS = /^\d+$/;
const NO_ZONE = /T\d{2}:\d{2}(:\d{2}(\.\d+)?)?$/;

/** Epoch ms from a number, a numeric string or an ISO date-time (zone-less = UTC); null otherwise. */
export function parseServerTime(v: unknown): number | null {
  if (typeof v === "number") return Number.isFinite(v) ? v : null;
  if (typeof v !== "string" || v === "") return null;
  if (DIGITS.test(v)) return Number(v);
  if (!v.includes("T")) return null;
  const ms = Date.parse(NO_ZONE.test(v) ? `${v}Z` : v);
  return Number.isNaN(ms) ? null : ms;
}

export function timelineOf(slot: StreamSlot): CastTimeline | null {
  if (slot.playStatus !== "PLAY" && slot.playStatus !== "PAUSE") return null;
  return {
    status: slot.playStatus,
    seekMs: Math.max(0, slot.seekTime ?? 0),
    startAt: parseServerTime(slot.playStartTime),
    rev: typeof slot.timelineRev === "number" ? slot.timelineRev : null,
  };
}

/** `PLAY ? seek + max(0, now − start) : seek`; a PLAY without a start holds at seek. */
export function positionAt(t: CastTimeline, serverNow: number): number {
  if (t.status !== "PLAY" || t.startAt == null) return t.seekMs;
  return t.seekMs + Math.max(0, serverNow - t.startAt);
}

export function sameTimeline(a: CastTimeline, b: CastTimeline): boolean {
  return (
    a.status === b.status &&
    a.seekMs === b.seekMs &&
    a.startAt === b.startAt &&
    a.rev === b.rev
  );
}

/** A cast answer's timeline (room-stc `timeline`), Android `CastTimelineDto.toTimeline`. */
export function timelineFromDto(d: { playStatus?: string; seekTime?: number; playStartTime?: number; rev?: number } | null | undefined): CastTimeline | null {
  if (!d || (d.playStatus !== "PLAY" && d.playStatus !== "PAUSE")) return null;
  return {
    status: d.playStatus,
    seekMs: Math.max(0, d.seekTime ?? 0),
    startAt: parseServerTime(d.playStartTime),
    rev: typeof d.rev === "number" ? d.rev : null,
  };
}

/** Rules R1/R2 (the follower's): a newer rev wins; without revs, a different timeline wins. */
export function acceptsTimeline(current: CastTimeline | null, next: CastTimeline): boolean {
  if (!current) return true;
  if (next.rev != null && current.rev != null) return next.rev > current.rev;
  return !sameTimeline(current, next);
}
